<template>
  <div class="info-page">
    <!-- 背景装饰 -->
    <div class="page-bg">
      <div class="bg-orb orb-1"></div>
      <div class="bg-orb orb-2"></div>
      <div class="bg-orb orb-3"></div>
    </div>

    <div class="info-layout">
      <!-- ===== 空状态：暂无算力产品 ===== -->
      <div v-if="!productList.length && loadingOver" class="empty-state">
        <div class="empty-icon">
          <svg
            viewBox="0 0 24 24"
            width="64"
            height="64"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
            <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01"></path>
          </svg>
        </div>
        <h2 class="empty-title">{{ $t("info.emptyTitle") }}</h2>
        <p class="empty-desc">{{ $t("info.emptyDesc") }}</p>
      </div>

      <template v-else>
        <!-- ===== 左侧：地区导航 ===== -->
        <aside class="region-nav">
          <!-- <div class="region-nav-head">
            <span class="rn-flag-dot">🌐</span>
            <span class="rn-head-title">Region</span>
          </div> -->
          <div class="region-list">
            <button
              v-for="item in productList"
              :key="item.id"
              class="region-item"
              :class="{ active: currentCountry === item.country_id }"
              @click="switchCountry(item.country_id)"
            >
              <img
                class="gqd rv-flag"
                :src="item.country_img"
                :alt="item.country_name"
              />
              <span class="rv-name">{{
                $t("country." + item.country_id)
              }}</span>
              <span class="rv-price"
                >${{ item.top_data?.latest_price ?? "--" }}</span
              >
            </button>
          </div>
        </aside>

        <!-- ===== 右侧：产品详情 ===== -->
        <main v-if="currentProduct" class="detail-wrap">
          <!-- 产品头部 -->
          <header class="prod-head">
            <div class="prod-copy">
              <h1 class="p-title">
                {{ $t("country." + currentProduct.country_id) }}
              </h1>
              <p class="p-tagline">
                {{ $t("token." + currentProduct.content + ".content.title") }}
              </p>
            </div>

            <div class="head-price">
              <span class="hp-label">{{ $t("info.latestPrice") }}</span>
              <strong class="hp-value"
                >${{ currentProduct.top_data?.latest_price ?? "-" }}</strong
              >
              <span
                class="hp-rate"
                :class="getIncreaseClass(currentProduct.top_data?.rate)"
              >
                {{ currentProduct.top_data?.rate ?? 0 }}%
              </span>
            </div>

            <div class="head-buy">
              <button class="buy-btn" @click="openPurchaseModal">
                <span>{{ $t("info.buyNow") }}</span>
                <span class="buy-arrow">→</span>
              </button>
            </div>
          </header>

          <!-- ===== 主内容：价格趋势 / 产品文档 ===== -->
          <section class="stage-card">
            <div class="stage-tabs">
              <button
                class="stage-tab"
                :class="{ active: mainTab === 'trend' }"
                @click="switchMainTab('trend')"
              >
                {{ $t("info.priceTrend") }}
              </button>
              <button
                class="stage-tab"
                :class="{ active: mainTab === 'docs' }"
                @click="switchMainTab('docs')"
              >
                {{ $t("info.docsTitle") }}
              </button>
            </div>

            <!-- ===== 价格趋势 ===== -->
            <div v-show="mainTab === 'trend'" class="stage-pane">
              <div class="tc-head">
                <div class="tc-titlebox">
                  <h2 class="tc-title">{{ $t("info.priceTrend") }}</h2>
                  <span class="tc-unit">{{ $t("info.unit") }}：$</span>
                </div>
                <div class="tc-tabs">
                  <button
                    v-for="range in chartRanges"
                    :key="range.value"
                    class="chart-range-btn"
                    :class="{ active: chartRange === range.value }"
                    @click="switchChartRange(range.value)"
                  >
                    {{ range.label }}
                  </button>
                </div>
              </div>

              <div class="chart-stage">
                <div ref="chartRef" class="chart-box"></div>
              </div>

              <div class="stat-row">
                <div class="stat-cell">
                  <span class="st-label">{{ $t("info.latestPrice") }}</span>
                  <span class="st-value st-live"
                    >${{ currentProduct.top_data?.latest_price ?? "-" }}</span
                  >
                </div>
                <div class="stat-cell">
                  <span class="st-label">{{ $t("info.lastPrice") }}</span>
                  <span class="st-value st-avg"
                    >${{ currentProduct.top_data?.last_price ?? "-" }}</span
                  >
                </div>
                <div class="stat-cell">
                  <span class="st-label">{{ $t("info.increase") }}</span>
                  <span
                    class="st-value"
                    :class="getIncreaseClass(currentProduct.top_data?.rate)"
                    >{{ currentProduct.top_data?.rate ?? 0 }}%</span
                  >
                </div>
              </div>
            </div>

            <!-- ===== 产品文档 ===== -->
            <div v-show="mainTab === 'docs'" class="stage-pane">
              <div class="doc-grid">
                <section class="doc-card">
                  <h3 class="doc-title">
                    <span class="doc-title-dot"></span>
                    <span>{{
                      $t("token." + currentProduct.content + ".content.title")
                    }}</span>
                  </h3>
                  <div class="doc-body intro-body">
                    <div
                      class="service-intro-content"
                      :class="{ expanded: introExpanded }"
                      v-html="
                        $t(
                          'token.' +
                            currentProduct.content +
                            '.content.content',
                        )
                      "
                    ></div>
                    <button
                      v-if="showIntroExpandBtn"
                      class="soft-btn"
                      @click="toggleIntroExpand"
                    >
                      <span>{{
                        introExpanded ? $t("info.collapse") : $t("info.expand")
                      }}</span>
                      <svg
                        class="chev"
                        :class="{ rotated: introExpanded }"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                </section>

                <section class="doc-card">
                  <h3 class="doc-title">
                    <span class="doc-title-dot"></span>
                    <span>{{
                      $t(
                        "token." +
                          currentProduct.content +
                          "." +
                          detailTab +
                          ".title",
                      )
                    }}</span>
                  </h3>
                  <div class="doc-body">
                    <div class="detail-tabs">
                      <button
                        v-for="tab in detailTabs"
                        :key="tab"
                        class="detail-tab"
                        :class="{ active: detailTab === tab }"
                        @click="switchDetailTab(tab)"
                      >
                        {{
                          $t(
                            "token." +
                              currentProduct.content +
                              "." +
                              tab +
                              ".title",
                          )
                        }}
                      </button>
                    </div>
                    <div class="desc-wrap">
                      <div
                        class="product-desc"
                        :class="{ expanded: isExpanded }"
                      >
                        <div
                          v-if="detailTab === 't1'"
                          class="detail-content"
                          v-html="
                            $t(
                              'token.' + currentProduct.content + '.t1.content',
                            )
                          "
                        ></div>
                        <div
                          v-else-if="detailTab === 't2'"
                          class="detail-content"
                          v-html="
                            $t(
                              'token.' + currentProduct.content + '.t2.content',
                            )
                          "
                        ></div>
                        <div
                          v-else-if="detailTab === 't3'"
                          class="detail-content"
                          v-html="
                            $t(
                              'token.' + currentProduct.content + '.t3.content',
                            )
                          "
                        ></div>
                        <div
                          v-else
                          class="detail-content"
                          v-html="
                            $t(
                              'token.' + currentProduct.content + '.t4.content',
                            )
                          "
                        ></div>
                      </div>
                      <button
                        v-if="showExpandBtn"
                        class="soft-btn desc-toggle"
                        @click="toggleExpand"
                      >
                        <span>{{
                          isExpanded
                            ? $t("info.collapseDetail")
                            : $t("info.expandDetail")
                        }}</span>
                        <svg
                          class="chev"
                          :class="{ rotated: isExpanded }"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </section>
        </main>
      </template>
    </div>

    <!-- ===== 购买弹窗 (Element UI) ===== -->
    <el-dialog
      :visible.sync="purchaseModalVisible"
      :title="$t('info.buyCompute')"
      width="800px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      @close="closePurchaseModal"
      class="purchase-dialog"
    >
      <div class="purchase-modal-content">
        <!-- 产品信息 -->
        <div class="product-info-bar">
          <img class="product-flag" :src="currentProduct?.country_img" alt="" />
          <span class="product-name-text">{{ currentProduct?.title }}</span>
          <span class="product-price-tag">${{ currentPrice }}</span>
        </div>

        <!-- 周期选择 -->
        <div class="modal-section">
          <div class="section-label">
            <span class="label-icon">📅</span>
            <span>{{ $t("info.selectPeriod") }}</span>
          </div>
          <div class="period-group-modal">
            <button
              v-for="period in periodOptions"
              :key="period.days"
              class="period-btn-modal"
              :class="{ active: selectedPeriod === period.days }"
              @click="selectPeriod(period.days)"
            >
              <div class="period-days-wrap">
                <span class="period-days-modal">{{ period.days }}</span>
                <span class="period-unit-modal">{{ $t("info.day") }}</span>
              </div>
              <span
                v-if="parseFloat(getPeriodDisplayRate(period)) !== 0"
                class="period-rate-badge"
                :class="
                  parseFloat(getPeriodDisplayRate(period)) > 0
                    ? 'rate-up'
                    : 'rate-down'
                "
              >
                <svg
                  v-if="parseFloat(getPeriodDisplayRate(period)) > 0"
                  class="rate-icon-modal"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
                <svg
                  v-else
                  class="rate-icon-modal"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
                  <polyline points="17 18 23 18 23 12"></polyline>
                </svg>
                <span class="rate-value-modal"
                  >{{ parseFloat(getPeriodDisplayRate(period)) > 0 ? "+" : ""
                  }}{{ getPeriodDisplayRate(period) }}%</span
                >
              </span>
            </button>
          </div>
        </div>

        <div style="display: flex; justify-content: space-between">
          <!-- 截止时间 -->
          <div class="modal-section deadline-section">
            <div class="section-label">
              <span class="label-icon">⏱️</span>
              <span>{{ $t("info.periodTime") }}</span>
            </div>
            <div class="deadline-modal">
              <span class="deadline-time-modal">{{ deadlineTime }}</span>
            </div>
          </div>

          <!-- 购买数量 -->
          <div class="modal-section">
            <div class="section-label">
              <span class="label-icon">🔢</span>
              <span>{{ $t("info.purchaseQuantity") }}</span>
            </div>
            <div class="quantity-control-modal">
              <button class="qty-btn-modal" @click="decreaseQuantity">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
              <input
                type="number"
                v-model.number="purchaseQuantity"
                class="qty-input-modal"
                min="1"
                :max="maxQuantity"
                @input="validateQuantity"
              />
              <button class="qty-btn-modal" @click="increaseQuantity">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 收益预览 -->
        <div class="section-label">
          <span class="label-icon">💰</span>
          <span>{{ $t("info.expectedProfit") }}</span>
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('info.profitTip')"
            placement="top"
          >
            <i class="el-icon-question xxb"></i>
          </el-tooltip>
        </div>
        <div class="modal-section profit-preview">
          <div class="profit-grid">
            <div class="profit-item-modal">
              <span class="profit-label-modal">
                {{ $t("info.baseProfit") }}
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="$t('info.settle24h')"
                  placement="top"
                >
                  <i class="el-icon-question xxb"></i>
                </el-tooltip>
              </span>
              <span class="profit-value-modal">${{ expectedProfit }}</span>
            </div>
            <div class="profit-divider-modal"></div>
            <div class="profit-item-modal period-profit-item">
              <span class="profit-label-modal"
                >{{ $t("info.periodProfit") }}
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="$t('info.settleDue')"
                  placement="top"
                >
                  <i class="el-icon-question xxb"></i>
                </el-tooltip>
              </span>
              <span class="profit-value-modal period-profit-modal"
                >${{ periodProfit }}</span
              >
              <!-- <span v-if="getPeriodRate() > 0" class="period-rate-highlight"
                >+{{ getPeriodRate() * 100 }}%</span
              > -->
            </div>
            <div class="profit-divider-modal"></div>
            <div class="profit-item-modal">
              <span class="profit-label-modal">{{
                $t("info.totalProfit")
              }}</span>
              <span class="profit-value-modal total-profit-modal"
                >${{ totalProfit }}</span
              >
            </div>
          </div>
        </div>

        <!-- ===== 金额汇总区域（替换原来的三个独立 total-bar） ===== -->
        <!-- 收益预览 -->
        <div class="section-label">
          <span class="label-icon">💰</span>
          <span>{{ $t("info.fundDetail") }}</span>
        </div>
        <div class="summary-section">
          <div class="summary-grid">
            <!-- 金额 -->
            <div class="summary-item">
              <span class="summary-label">
                <span class="label-icon">💰</span>
                {{ $t("info.purchaseAmount") }}
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="$t('info.buyFormula')"
                  placement="top"
                >
                  <i class="el-icon-question xxb"></i>
                </el-tooltip>
              </span>
              <span class="summary-value">${{ purchaseTotal }}</span>
            </div>

            <!-- 手续费 -->
            <div class="summary-item">
              <span class="summary-label">
                <span class="label-icon">⚡</span>
                {{ $t("info.fee") }}
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="
                    $t('info.feeFormula', {
                      fee: (currentProduct?.buy_fee * 100).toFixed(2),
                    })
                  "
                  placement="top"
                >
                  <i class="el-icon-question xxb"></i>
                </el-tooltip>
              </span>
              <span class="summary-value fee-value">${{ purchaseFee }}</span>
            </div>

            <!-- 总计（高亮） -->
            <div class="summary-item total-item">
              <span class="summary-label">
                <span class="label-icon">🏷️</span>
                {{ $t("info.total") }}
              </span>
              <span class="summary-value total-value"
                >${{
                  (parseFloat(purchaseTotal) + parseFloat(purchaseFee)).toFixed(
                    2,
                  )
                }}</span
              >
            </div>
          </div>
        </div>

        <!-- 可用余额（独立一行） -->
        <div class="summary-item balance-item">
          <span class="summary-label">
            <span class="label-icon">💰</span>
            {{ $t("memberBox.availableBalance") }}
          </span>
          <span class="summary-value balance-value">${{ walletBalance }}</span>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closePurchaseModal">{{
          $t("info.cancel")
        }}</el-button>
        <el-button
          type="primary"
          @click="handleBuy"
          :loading="buyLoading"
          :disabled="buyLoading"
        >
          {{ $t("info.confirmBuy") }}
        </el-button>
      </span>
    </el-dialog>

    <!-- ===== 交易密码弹窗 ===== -->
    <TradePasswordModal
      :visible.sync="passwordModalVisible"
      :product-info="passwordProductInfo"
      :loading="buyLoading"
      :error="passwordError"
      :title="$t('info.tradeConfirm')"
      :label-text="$t('info.enterPassword')"
      :placeholder="$t('info.enterPassword')"
      :confirm-text="$t('info.confirmPay')"
      :cancel-text="$t('info.cancel')"
      @confirm="handlePasswordConfirm"
      @cancel="handlePasswordCancel"
      @close="handlePasswordClose"
    />
  </div>
</template>

<script>
import * as echarts from "echarts";
import TradePasswordModal from "@/components/toast/TradePasswordModal.vue";

export default {
  name: "InfoView",
  components: {
    TradePasswordModal,
  },
  data() {
    return {
      code: "",
      productList: [],
      currentCountry: null,
      chartInstance: null,
      resizeTimer: null,

      // ===== 主内容 Tab：trend=价格趋势 / docs=产品文档 =====
      mainTab: "trend",

      // ===== 趋势图时间范围 =====
      chartRange: 30,
      chartRanges: [
        { label: this.$t("info.chart7d"), value: 7 },
        { label: this.$t("info.chart15d"), value: 15 },
        { label: this.$t("info.chart30d"), value: 30 },
      ],

      // ===== 购买相关 =====
      purchaseQuantity: 1,
      selectedPeriod: 3,
      maxQuantity: 9999,

      // ===== 弹窗控制 =====
      purchaseModalVisible: false,
      passwordModalVisible: false,
      buyLoading: false,
      passwordError: "",

      // ===== 展开/收起 =====
      isExpanded: false,
      showExpandBtn: false,

      // ===== 产品服务介绍文案展开/收起 =====
      introExpanded: false,
      showIntroExpandBtn: false,

      // ===== 产品服务详情菜单 =====
      detailTab: "t1",
      detailTabs: ["t1", "t2", "t3", "t4"],

      //加载数据中
      loadingOver: false,

      // ===== 钱包可用余额 =====
      wallet: {},
    };
  },
  computed: {
    currentProduct() {
      if (!this.productList.length) return null;
      return (
        this.productList.find(
          (item) => item.country_id === this.currentCountry,
        ) || this.productList[0]
      );
    },
    periodOptions() {
      return this.currentProduct?.period || [];
    },
    currentPrice() {
      return parseFloat(this.currentProduct?.top_data?.latest_price) || 0;
    },

    // ===== 密码弹窗产品信息 =====
    passwordProductInfo() {
      if (!this.currentProduct) return null;
      return {
        img: this.currentProduct.country_img,
        name: this.currentProduct.title,
        price: this.currentPrice.toFixed(2),
      };
    },

    purchaseTotal() {
      return (this.currentPrice * this.purchaseQuantity).toFixed(2);
    },

    purchaseFee() {
      return (this.purchaseTotal * this.currentProduct?.buy_fee || 0).toFixed(
        2,
      );
    },

    expectedProfit() {
      const avgIncome = parseFloat(this.currentProduct?.avg_income) || 0;
      if (avgIncome <= 0 || this.purchaseQuantity <= 0) {
        return "0.00";
      }

      const days = this.selectedPeriod;
      // 预计收益 = 天数 × 平均收益 × 购买数量
      const result = days * avgIncome * this.purchaseQuantity;
      return result.toFixed(2);
    },

    periodProfit() {
      const expected = parseFloat(this.expectedProfit) || 0;
      const rate = this.getPeriodRate();
      const result = expected * rate;
      return result.toFixed(2);
    },

    totalProfit() {
      const expected = parseFloat(this.expectedProfit) || 0;
      const period = parseFloat(this.periodProfit) || 0;
      return (expected + period).toFixed(2);
    },

    deadlineTime() {
      const days = this.selectedPeriod;
      const deadline = this.$dayjs().add(days, "day");

      return deadline.toDate().toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    walletBalance() {
      const val = parseFloat(this.wallet?.balance);
      return isNaN(val) ? "0.00" : val.toFixed(2);
    },
  },
  watch: {
    currentCountry: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
          this.isExpanded = false;
          this.checkDescHeight();
        });
      },
      immediate: true,
    },
    productList: {
      handler() {
        if (this.productList.length) {
          this.$nextTick(() => {
            this.updateChart();
            this.checkDescHeight();
          });
        }
      },
      deep: true,
    },
  },
  created() {
    this.code = this.$route.query.code || "";
    this.getWallet();
    this.getTokenList();
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
      this.checkIntroHeight();
    });
  },

  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    getWallet() {
      this.$api.userWallet
        .getWallet()
        .then((res) => {
          this.wallet = res.data || {};
        })
        .catch(() => {
          this.wallet = {};
        });
    },
    getPeriodRate() {
      const period = this.periodOptions.find(
        (p) => p.days === this.selectedPeriod,
      );
      return period ? period.rate : 0;
    },

    // ===== 计算周期按钮显示的收益率 =====
    // 默认显示 avg_rate，如果周期 rate > 0，则显示 avg_rate + avg_rate * period.rate
    getPeriodDisplayRate(period) {
      const avgRate = parseFloat(this.currentProduct?.avg_rate) || 0;
      if (avgRate <= 0) return 0;

      if (period.rate > 0) {
        return ((avgRate + avgRate * period.rate) * 100).toFixed(2);
      }
      return (avgRate * 100).toFixed(2);
    },

    async getTokenList() {
      try {
        const res = await this.$api.token.getTokenList();
        this.loadingOver = true;
        if (res.code === 1) {
          this.$message.warning(res.msg);
          return;
        }
        this.productList = res.data || [];

        if (this.code) {
          const matched = this.productList.find(
            (item) => item.code === this.code,
          );
          this.currentCountry =
            matched?.country_id || this.productList[0]?.country_id || null;
        } else {
          this.currentCountry = this.productList[0]?.country_id || null;
        }

        //初始化周期
        if (this.currentProduct?.period?.length) {
          this.selectedPeriod = this.currentProduct.period[0].days;
        }

        this.$nextTick(() => {
          this.checkDescHeight();
        });
      } catch (error) {
        console.error("获取产品列表失败:", error);
        this.$message.error(this.$t("info.fetchFail"));
      }
    },

    switchCountry(id) {
      if (this.currentCountry === id) return;
      this.currentCountry = id;
      this.resetPurchase();
    },

    // ===== 切换主内容 Tab =====
    switchMainTab(tab) {
      if (this.mainTab === tab) return;
      this.mainTab = tab;
      if (tab === "trend") {
        this.$nextTick(() => {
          this.updateChart();
        });
      } else {
        this.isExpanded = false;
        this.$nextTick(() => {
          this.checkDescHeight();
          this.checkIntroHeight();
        });
      }
    },

    // ===== 切换趋势图时间范围 =====
    switchChartRange(range) {
      if (this.chartRange === range) return;
      this.chartRange = range;
      this.updateChart();
    },

    // ===== 弹窗控制 =====
    openPurchaseModal() {
      if (!this.currentProduct) {
        this.$message.warning(this.$t("info.selectProduct"));
        return;
      }
      this.purchaseModalVisible = true;
    },

    closePurchaseModal() {
      this.purchaseModalVisible = false;
    },

    // ===== 购买数量控制 =====
    increaseQuantity() {
      if (this.purchaseQuantity < this.maxQuantity) {
        this.purchaseQuantity++;
      }
    },
    decreaseQuantity() {
      if (this.purchaseQuantity > 1) {
        this.purchaseQuantity--;
      }
    },
    validateQuantity() {
      if (this.purchaseQuantity < 1) {
        this.purchaseQuantity = 1;
      }
      if (this.purchaseQuantity > this.maxQuantity) {
        this.purchaseQuantity = this.maxQuantity;
      }
    },

    selectPeriod(days) {
      this.selectedPeriod = days;
    },

    resetPurchase() {
      this.purchaseQuantity = 1;

      //找到默认周期
      if (this.currentProduct?.period?.length) {
        this.selectedPeriod = this.currentProduct.period[0].days;
      }
    },

    // ===== 确认购买（打开密码弹窗） =====
    handleBuy() {
      if (this.purchaseQuantity < 1) {
        this.$message.warning(this.$t("info.quantityMin"));
        return;
      }
      if (this.currentPrice <= 0) {
        this.$message.warning(this.$t("info.priceError"));
        return;
      }
      // 清空之前的错误和密码
      this.passwordError = "";
      // 打开密码弹窗
      this.passwordModalVisible = true;
    },

    // ===== 密码确认回调 =====
    async handlePasswordConfirm(password) {
      this.buyLoading = true;
      this.passwordError = "";

      const params = {
        code: this.currentProduct.code,
        quantity: this.purchaseQuantity,
        period: this.selectedPeriod,
        trade_password: password,
      };

      try {
        const res = await this.$api.token.buyToken(params);
        if (res.code === 0) {
          this.$message.success(res.msg || this.$t("info.buySuccess"));
          this.passwordModalVisible = false;
          this.purchaseModalVisible = false;
          await this.getTokenList();
        } else {
          // 密码错误或其他错误
          this.passwordError = res.msg || this.$t("info.tradeFail");
          // 如果是密码错误，让用户重新输入
          if (res.code === 1002 || res.msg?.includes("密码")) {
            this.passwordError = res.msg || this.$t("info.passwordError");
          }
        }
      } catch (err) {
        console.error("购买失败:", err);
        this.passwordError = this.$t("info.buyFail");
      } finally {
        this.buyLoading = false;
      }
    },

    // ===== 密码取消回调 =====
    handlePasswordCancel() {
      this.passwordModalVisible = false;
      this.passwordError = "";
    },

    // ===== 密码弹窗关闭回调 =====
    handlePasswordClose() {
      this.passwordModalVisible = false;
      this.passwordError = "";
    },

    // ===== 产品服务介绍文案展开/收起 =====
    toggleIntroExpand() {
      this.introExpanded = !this.introExpanded;
    },

    // ===== 切换产品服务详情菜单 =====
    switchDetailTab(tab) {
      if (this.detailTab === tab) return;
      this.detailTab = tab;
      this.isExpanded = false;
      this.$nextTick(() => {
        this.checkDescHeight();
      });
    },

    // ===== 展开/收起 =====
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    checkDescHeight() {
      const descEl = this.$el?.querySelector(".product-desc");
      if (descEl) {
        descEl.style.maxHeight = "none";
        const height = descEl.scrollHeight;
        descEl.style.maxHeight = "";
        this.showExpandBtn = height > 300;
      }
    },

    // ===== 检测产品服务介绍文案是否需要展开按钮 =====
    checkIntroHeight() {
      const introEl = this.$el?.querySelector(".service-intro-content");
      if (introEl) {
        introEl.style.maxHeight = "none";
        const height = introEl.scrollHeight;
        introEl.style.maxHeight = "";
        this.showIntroExpandBtn = height > 120;
      }
    },

    getIncreaseClass(value) {
      if (value === null || value === undefined) return "";
      const num = parseFloat(value);
      if (isNaN(num)) return "";
      return num >= 0 ? "increase-up" : "increase-down";
    },

    initChart() {
      if (!this.$refs.chartRef) {
        setTimeout(() => {
          this.initChart();
        }, 300);
        return;
      }

      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }

      this.chartInstance = echarts.init(this.$refs.chartRef);
      this.updateChart();
      this.handleResize();
    },

    updateChart() {
      if (!this.chartInstance || !this.currentProduct) return;

      const trendData = this.currentProduct.top_trend || [];
      // 根据 chartRange 过滤最近 N 天数据
      const filteredData = trendData.slice(-this.chartRange);
      const dates = filteredData.map((item) => item.date || "");
      const prices = filteredData.map((item) => item.price ?? 0);

      const hasData = prices.some((v) => v > 0);
      const maxPrice = Math.max(...prices, 0);
      const minPrice = Math.min(...prices, 0);

      const option = {
        tooltip: {
          trigger: "axis",
          formatter: (params) => {
            const p = params[0];
            return `<strong>${p.name}</strong><br/>${this.$t(
              "info.price",
            )}：<strong>$${p.value}</strong>`;
          },
          backgroundColor: "rgba(10, 14, 26, 0.85)",
          borderColor: "rgba(0, 180, 255, 0.2)",
          borderWidth: 1,
          textStyle: { color: "#fff", fontSize: 12 },
        },
        grid: {
          left: 50,
          right: 20,
          bottom: 32,
          top: 16,
        },
        xAxis: {
          type: "category",
          data: dates.length ? dates : [this.$t("info.noData")],
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.08)" } },
          axisLabel: {
            color: "rgba(255,255,255,0.35)",
            fontSize: 10,
          },
          axisTick: { show: false },
          boundaryGap: false,
        },
        yAxis: {
          type: "value",
          splitLine: {
            lineStyle: {
              color: "rgba(255,255,255,0.05)",
              type: "dashed",
            },
          },
          axisLabel: {
            color: "rgba(255,255,255,0.35)",
            fontSize: 10,
            formatter: (value) => "$" + value,
          },
          name: this.$t("info.price"),
          nameTextStyle: {
            color: "rgba(255,255,255,0.2)",
            fontSize: 10,
          },
          min: hasData ? (minPrice > 0 ? minPrice * 0.8 : -0.5) : -0.5,
          max: hasData ? (maxPrice < 5 ? maxPrice + 1 : maxPrice * 1.15) : 5,
        },
        series: [
          {
            name: this.$t("info.price"),
            type: "line",
            smooth: true,
            data: prices.length ? prices : [0],
            symbol: "circle",
            symbolSize: 5,
            lineStyle: {
              color: "#00b4ff",
              width: 2.5,
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(0, 180, 255, 0.4)" },
                { offset: 0.5, color: "rgba(0, 100, 255, 0.12)" },
                { offset: 1, color: "rgba(0, 180, 255, 0.02)" },
              ]),
            },
            itemStyle: {
              color: "#00b4ff",
              borderColor: "#0a0e1a",
              borderWidth: 1.5,
            },
            markPoint: {
              data: hasData
                ? [
                    {
                      type: "max",
                      name: this.$t("info.maxValue"),
                      symbol: "circle",
                      symbolSize: 10,
                      label: {
                        formatter: (params) => "$" + params.value,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 600,
                        position: "top",
                      },
                      itemStyle: {
                        color: "#7c3aed",
                        borderColor: "#0a0e1a",
                        borderWidth: 2,
                      },
                    },
                  ]
                : [],
            },
          },
        ],
      };

      this.chartInstance.setOption(option, true);
      this.chartInstance.resize();
    },

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.chartInstance) {
          this.chartInstance.resize();
        }
      }, 100);
    },

    destroyChart() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = null;
      }
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }
    },
  },
};
</script>
<style scoped>
/* ==========================================================
   GPU-Ass · 算力产品详情（v3）
   左导航 + 右侧详情式布局；所有文案均可安全换行
   ========================================================== */
.info-page {
  --bg: #0a0e1a;
  --panel: rgba(255, 255, 255, 0.045);
  --panel-2: rgba(255, 255, 255, 0.07);
  --line: rgba(255, 255, 255, 0.09);
  --line-strong: rgba(0, 180, 255, 0.35);
  --text: #f4f7ff;
  --dim: rgba(235, 240, 255, 0.62);
  --weak: rgba(235, 240, 255, 0.38);
  --accent: #38bdf8;
  --accent-deep: #2563eb;
  --grad: linear-gradient(135deg, #38bdf8, #2563eb);
  --green: #34d399;
  --red: #fb7185;
  --amber: #fbbf24;
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
  padding: 40px 22px 72px;
  background: radial-gradient(
      1100px 560px at 85% -8%,
      rgba(37, 99, 235, 0.16),
      transparent 60%
    ),
    radial-gradient(
      820px 460px at -8% 108%,
      rgba(124, 58, 237, 0.14),
      transparent 60%
    ),
    var(--bg);
  overflow-x: hidden;
}
.info-page *,
.info-page *::before,
.info-page *::after {
  box-sizing: border-box;
}
.info-page h1,
.info-page h2,
.info-page h3,
.info-page h4,
.info-page p,
.info-page span,
.info-page button,
.info-page div {
  overflow-wrap: break-word;
  word-break: break-word;
}
button {
  font-family: inherit;
}

.page-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}
.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
}
.orb-1 {
  width: 380px;
  height: 380px;
  background: #2563eb;
  top: -140px;
  right: -80px;
  opacity: 0.13;
}
.orb-2 {
  width: 280px;
  height: 280px;
  background: #7c3aed;
  bottom: -100px;
  left: -80px;
  opacity: 0.1;
}
.orb-3 {
  width: 320px;
  height: 320px;
  background: #0ea5e9;
  top: 36%;
  left: 42%;
  opacity: 0.05;
}

/* ===== 主体布局：左导航 + 右详情 ===== */
.info-layout {
  position: relative;
  z-index: 1;
  max-width: 1300px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 236px minmax(0, 1fr);
  gap: 22px;
  align-items: start;
}

/* ===== 空状态（占满两列） ===== */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 440px;
  text-align: center;
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.02)
  );
  border: 1px solid var(--line);
  border-radius: 20px;
  padding: 48px 28px;
}
.empty-icon {
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(56, 189, 248, 0.07);
  border: 1px solid rgba(56, 189, 248, 0.15);
  color: rgba(56, 189, 248, 0.55);
  margin-bottom: 22px;
  animation: empty-float 3s ease-in-out infinite;
}
.empty-title {
  font-size: 21px;
  font-weight: 700;
  margin: 0 0 10px;
  line-height: 1.4;
  max-width: 520px;
}
.empty-desc {
  font-size: 14px;
  line-height: 1.7;
  color: var(--weak);
  max-width: 520px;
  margin: 0;
}
@keyframes empty-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* ===== 左侧地区导航 ===== */
.region-nav {
  position: sticky;
  top: 22px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 44px);
  height: 100%;
  background: linear-gradient(
    175deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.028)
  );
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 14px 12px 16px;
}
.region-nav-head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px 14px;
}
.rn-flag-dot {
  font-style: normal;
  font-size: 18px;
  line-height: 1;
}
.rn-head-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: var(--weak);
}
.region-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 6px;
}
.region-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  align-content: center;
  flex: 1;
  gap: 10px;
  width: 100%;
  padding: 11px 12px;
  text-align: left;
  border-radius: 13px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--dim);
  cursor: pointer;
  transition: all 0.22s ease;
}
.region-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
}
.region-item.active {
  background: linear-gradient(
    90deg,
    rgba(56, 189, 248, 0.16),
    rgba(37, 99, 235, 0.05)
  );
  border-color: rgba(56, 189, 248, 0.32);
  color: #fff;
}
.rv-flag {
  width: 24px;
  height: 17px;
  border-radius: 3px;
  object-fit: cover;
}
.rv-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}
.region-item.active .rv-name {
  font-weight: 600;
}
.rv-price {
  font-size: 12px;
  color: var(--weak);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.region-item.active .rv-price {
  color: var(--accent);
}

/* ===== 右侧详情 ===== */
.detail-wrap {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
}

/* 产品头部 */
.prod-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 26px;
  padding: 26px 30px;
  border-radius: 20px;
  background: linear-gradient(120deg, rgba(56, 189, 248, 0.08), transparent 45%),
    linear-gradient(
      175deg,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0.024)
    );
  border: 1px solid var(--line);
  overflow: hidden;
  position: relative;
}
.prod-head::after {
  content: "";
  position: absolute;
  left: 30px;
  bottom: 0;
  height: 1px;
  width: 46%;
  background: linear-gradient(90deg, rgba(56, 189, 248, 0.5), transparent);
}
.prod-copy {
  min-width: 0;
}
.p-title {
  margin: 0;
  font-size: clamp(25px, 2.4vw, 34px);
  font-weight: 800;
  line-height: 1.22;
  letter-spacing: 0.2px;
}
.p-tagline {
  margin: 8px 0 0;
  font-size: 14.5px;
  color: rgba(56, 189, 248, 0.75);
  font-weight: 500;
  line-height: 1.6;
}

.head-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  min-width: 0;
}
.hp-label {
  font-size: 12.5px;
  color: var(--weak);
  line-height: 1.4;
  text-align: right;
}
.hp-value {
  font-size: clamp(26px, 2.6vw, 34px);
  font-weight: 800;
  line-height: 1.15;
  color: #fff;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.hp-rate {
  padding: 3px 11px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}
.hp-rate.increase-up {
  color: var(--green);
  background: rgba(52, 211, 153, 0.12);
}
.hp-rate.increase-down {
  color: var(--red);
  background: rgba(251, 113, 133, 0.12);
}
.hp-rate:not(.increase-up):not(.increase-down) {
  color: var(--dim);
  background: rgba(255, 255, 255, 0.06);
}

.head-buy {
  display: flex;
  align-items: center;
}
.buy-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 168px;
  height: 50px;
  padding: 0 26px;
  border: none;
  border-radius: 14px;
  background: var(--grad);
  color: #fff;
  font-size: 15.5px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 12px 30px rgba(37, 99, 235, 0.34);
}
.buy-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 38px rgba(56, 189, 248, 0.4);
}
.buy-arrow {
  font-size: 17px;
  line-height: 1;
}

/* 走势卡片 */
.trend-card {
  padding: 24px 30px 28px;
  border-radius: 20px;
  background: linear-gradient(
    175deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.02)
  );
  border: 1px solid var(--line);
}
.tc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}
.tc-titlebox {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}
.tc-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
}
.tc-unit {
  font-size: 12.5px;
  color: var(--weak);
  white-space: nowrap;
}
.tc-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--line);
  border-radius: 999px;
  overflow-x: auto;
  max-width: 100%;
  scrollbar-width: none;
}
.tc-tabs::-webkit-scrollbar {
  display: none;
}
.chart-range-btn {
  flex: 0 0 auto;
  padding: 7px 16px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--weak);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.22s ease;
  white-space: nowrap;
}
.chart-range-btn:hover {
  color: var(--text);
}
.chart-range-btn.active {
  background: var(--grad);
  color: #fff;
}

.chart-stage {
  width: 100%;
  min-width: 0;
  height: clamp(310px, 21vw, 500px);
  margin: 20px 0 16px;
}
.chart-box {
  width: 100%;
  height: 100%;
}

/* 行情指标 */
.stat-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.stat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 13px 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  text-align: center;
}
.st-label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12.5px;
  color: var(--weak);
  line-height: 1.4;
  min-height: 2.6em;
}
.st-value {
  font-size: clamp(16px, 1.7vw, 20px);
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.st-value.st-live {
  color: var(--green);
}
.st-value.st-avg {
  color: var(--accent);
}
.st-value.increase-up {
  color: var(--green);
}
.st-value.increase-down {
  color: var(--red);
}

/* ===== 介绍 / 详情 双栏 ===== */
.doc-grid {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
}
.doc-card {
  min-width: 0;
  padding: 22px 24px;
  border-radius: 20px;
  background: linear-gradient(
    175deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.02)
  );
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;
}
.doc-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  font-size: 15.5px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.5;
}
.doc-title-dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 12px rgba(56, 189, 248, 0.8);
}
.doc-body {
  min-width: 0;
  font-size: 14px;
  line-height: 1.85;
  color: var(--dim);
}

/* 简介富文本 */
.service-intro-content {
  max-height: 150px;
  overflow: hidden;
  position: relative;
}
.service-intro-content.expanded {
  max-height: none;
}
.service-intro-content >>> p {
  margin: 0 0 10px;
}
.service-intro-content >>> p:last-child {
  margin-bottom: 0;
}
.service-intro-content >>> ul,
.service-intro-content >>> ol {
  margin: 6px 0 12px;
  padding-left: 20px;
}
.service-intro-content >>> li {
  margin: 4px 0;
}
.service-intro-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
}
.service-intro-content >>> strong {
  color: #fff;
}
.service-intro-content >>> a {
  color: var(--accent);
}

/* 折叠按钮 */
.soft-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.08);
  border: 1px solid rgba(56, 189, 248, 0.2);
  color: var(--accent);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.22s ease;
}
.soft-btn:hover {
  background: rgba(56, 189, 248, 0.16);
}
.chev {
  flex: 0 0 auto;
  transition: transform 0.25s ease;
}
.chev.rotated {
  transform: rotate(180deg);
}

/* 详情菜单 */
.detail-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.detail-tab {
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--weak);
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.22s ease;
  line-height: 1.4;
}
.detail-tab:hover {
  color: var(--text);
  background: rgba(255, 255, 255, 0.08);
}
.detail-tab.active {
  color: #fff;
  background: var(--grad);
  border-color: transparent;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25);
}

/* 详情富文本 */
.desc-wrap {
  min-width: 0;
}
.product-desc {
  max-height: 300px;
  overflow: hidden;
}
.product-desc.expanded {
  max-height: none;
}
.product-desc >>> p {
  margin: 0 0 12px;
}
.product-desc >>> p:last-child {
  margin-bottom: 0;
}
.product-desc >>> ul,
.product-desc >>> ol {
  margin: 6px 0 14px;
  padding-left: 22px;
}
.product-desc >>> li {
  margin: 5px 0;
}
.product-desc >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
}
.product-desc >>> table {
  max-width: 100%;
  border-collapse: collapse;
}
.product-desc >>> strong {
  color: #fff;
  font-weight: 600;
}
.product-desc >>> h1,
.product-desc >>> h2,
.product-desc >>> h3,
.product-desc >>> h4 {
  color: #fff;
  margin: 16px 0 8px;
  line-height: 1.4;
}
.product-desc >>> a {
  color: var(--accent);
}
.desc-toggle {
  margin-top: 16px;
}

/* ===== 购买弹窗 ===== */
.purchase-modal-content {
  padding: 4px 0;
}

.product-info-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(0, 180, 255, 0.06);
  border-radius: 12px;
  border: 1px solid rgba(0, 180, 255, 0.08);
  margin-bottom: 20px;
}
.product-flag {
  width: 32px;
  height: 22px;
  border-radius: 4px;
  object-fit: cover;
}
.product-name-text {
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
}
.product-price-tag {
  font-size: 18px;
  font-weight: 800;
  color: #00b4ff;
  font-family: "SF Mono", "Courier New", monospace;
}

.modal-section {
  margin-bottom: 18px;
}
.modal-section:last-of-type {
  margin-bottom: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
}
.label-icon {
  font-size: 16px;
}

/* ===== 周期选择 ===== */
.period-group-modal {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.period-btn-modal {
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.03);
  border: 1.5px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;
  min-width: 0;
}
.period-btn-modal::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #f44336, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}
.period-btn-modal:hover {
  background: rgb(244 67 54 / 7%);
  border-color: rgb(244 67 54 / 24%);
  color: #f44336;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(244, 67, 54, 0.1);
}
.period-btn-modal:hover::before {
  opacity: 1;
}
.period-btn-modal.active {
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.15),
    rgba(0, 100, 255, 0.08)
  );
  border-color: #00b4ff;
  color: #00b4ff;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 100, 255, 0.2);
}
.period-btn-modal.active::before {
  background: linear-gradient(90deg, transparent, #00b4ff, transparent);
  opacity: 1;
}

.period-days-wrap {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.period-days-modal {
  font-size: 16px;
  font-weight: 800;
}
.period-unit-modal {
  font-size: 11px;
  font-weight: 500;
}
.period-btn-modal.active .period-unit-modal {
  color: rgba(0, 180, 255, 0.5);
}

/* ===== 额外收益率徽章（更突出） ===== */
.period-rate-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px 2px 6px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(0, 201, 81, 0.15),
    rgba(0, 201, 81, 0.08)
  );
  color: #00c951;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 201, 81, 0.18);
}
.period-rate-badge .rate-icon-modal {
  flex-shrink: 0;
  stroke: #00c951;
}
/* 下跌（负值）：红色 */
.period-rate-badge.rate-down {
  background: linear-gradient(
    135deg,
    rgba(244, 67, 54, 0.15),
    rgba(244, 67, 54, 0.08)
  );
  color: #f44336;
  border-color: rgba(244, 67, 54, 0.18);
}
.period-rate-badge.rate-down .rate-icon-modal {
  stroke: #f44336;
}
.rate-value-modal {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
.period-btn-modal:hover .period-rate-badge.rate-up {
  background: linear-gradient(
    135deg,
    rgba(0, 201, 81, 0.22),
    rgba(0, 201, 81, 0.1)
  );
  border-color: rgba(0, 201, 81, 0.32);
  transform: scale(1.08);
  box-shadow: 0 4px 16px rgba(0, 201, 81, 0.2);
}
.period-btn-modal:hover .period-rate-badge.rate-down {
  background: linear-gradient(
    135deg,
    rgba(244, 67, 54, 0.25),
    rgba(244, 67, 54, 0.12)
  );
  border-color: rgba(244, 67, 54, 0.3);
  transform: scale(1.08);
  box-shadow: 0 4px 16px rgba(244, 67, 54, 0.2);
}
.period-btn-modal.active .period-rate-badge {
  background: linear-gradient(
    135deg,
    rgba(0, 180, 255, 0.25),
    rgba(0, 100, 255, 0.12)
  );
  border-color: rgba(0, 180, 255, 0.3);
  color: #00b4ff;
  box-shadow: 0 4px 16px rgba(0, 100, 255, 0.2);
}
.period-btn-modal.active .period-rate-badge .rate-icon-modal {
  stroke: #00b4ff;
}
.period-btn-modal.active .rate-value-modal {
  color: #00b4ff;
}

/* ===== 截止时间 ===== */
.deadline-section {
  margin-bottom: 18px;
}

.deadline-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border-radius: 10px;
  background: rgba(255, 215, 0, 0.04);
  border: 1.5px solid rgba(255, 215, 0, 0.08);
  transition: all 0.3s ease;
  width: fit-content;
}
.deadline-modal:hover {
  border-color: rgba(255, 215, 0, 0.15);
  background: rgba(255, 215, 0, 0.06);
}
.deadline-time-modal {
  font-size: 16px;
  font-weight: 700;
  color: #ffd700;
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
}

/* ===== 数量控制 ===== */
.quantity-control-modal {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  overflow: hidden;
  border: 1.5px solid rgba(255, 255, 255, 0.06);
  width: fit-content;
}
.quantity-control-modal:hover {
  border-color: rgba(255, 255, 255, 0.12);
}
.quantity-control-modal:focus-within {
  border-color: rgba(0, 180, 255, 0.3);
  box-shadow: 0 0 0 3px rgba(0, 180, 255, 0.05);
}

.qty-btn-modal {
  width: 38px;
  height: 38px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qty-btn-modal:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
.qty-btn-modal:active {
  transform: scale(0.92);
}

.qty-input-modal {
  width: 56px;
  height: 38px;
  background: transparent;
  border: none;
  border-left: 1.5px solid rgba(255, 255, 255, 0.06);
  border-right: 1.5px solid rgba(255, 255, 255, 0.06);
  color: #fff;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  outline: none;
  -moz-appearance: textfield;
  font-family: "SF Mono", "Courier New", monospace;
}
.qty-input-modal::-webkit-outer-spin-button,
.qty-input-modal::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

/* ===== 收益预览 ===== */
.profit-preview {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.profit-grid {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profit-item-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}
.profit-label-modal {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
}
.profit-value-modal {
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  font-family: "SF Mono", "Courier New", monospace;
  margin-top: 2px;
}
.profit-value-modal.period-profit-modal {
  color: #a78bfa;
}
.profit-value-modal.total-profit-modal {
  color: #34d399;
}
.profit-divider-modal {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.06);
}

/* ===== 周期收益百分比高亮 ===== */
.period-profit-item {
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(167, 139, 250, 0.08),
    rgba(124, 58, 237, 0.04)
  );
  border-radius: 10px;
  padding: 8px 12px;
  border: 1px solid rgba(167, 139, 250, 0.12);
}
.period-rate-highlight {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 15px;
  font-weight: 800;
  color: #a78bfa;
  background: linear-gradient(
    135deg,
    rgba(167, 139, 250, 0.2),
    rgba(124, 58, 237, 0.1)
  );
  border: 1px solid rgba(167, 139, 250, 0.25);
  letter-spacing: 0.5px;
  box-shadow: 0 2px 12px rgba(124, 58, 237, 0.15);
}

/* ===== 总计 ===== */
.total-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: rgba(255, 215, 0, 0.04);
  border-radius: 12px;
  border: 1px solid rgba(255, 215, 0, 0.06);
}
.total-label-modal {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
}
.total-price-modal {
  font-size: 24px;
  font-weight: 900;
  color: #ffd700;
  font-family: "SF Mono", "Courier New", monospace;
}

/* ===== 购买弹窗样式覆盖 ===== */
.purchase-dialog >>> .el-dialog {
  background: #1a1e2e;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 5vh !important;
}
.purchase-dialog >>> .el-dialog__header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  padding: 18px 24px;
}
.purchase-dialog >>> .el-dialog__title {
  color: #fff;
  font-size: 17px;
  font-weight: 700;
}
.purchase-dialog >>> .el-dialog__headerbtn .el-dialog__close {
  color: rgba(255, 255, 255, 0.4);
}
.purchase-dialog >>> .el-dialog__headerbtn .el-dialog__close:hover {
  color: #fff;
}
.purchase-dialog >>> .el-dialog__body {
  padding: 20px 24px;
}
.purchase-dialog >>> .el-dialog__footer {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 14px 24px;
}
.purchase-dialog >>> .el-button--default {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
.purchase-dialog >>> .el-button--default:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #fff;
}
.purchase-dialog >>> .el-button--primary {
  background: linear-gradient(135deg, #00b4ff, #0066ff);
  border: none;
}
.purchase-dialog >>> .el-button--primary:hover {
  opacity: 0.85;
}
.purchase-dialog >>> .el-button--primary.is-disabled {
  background: rgba(0, 100, 255, 0.4);
  border: none;
}

/* ===== 弹窗小屏适配 ===== */
@media (max-width: 600px) {
  .purchase-dialog >>> .el-dialog__body {
    padding: 14px 16px;
  }
  .purchase-dialog >>> .el-dialog__footer {
    padding: 12px 16px 16px;
    display: flex;
    gap: 10px;
  }
  .purchase-dialog >>> .dialog-footer .el-button {
    flex: 1;
    margin-left: 0;
  }
  .period-group-modal {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ===== 金额汇总区域 ===== */
.summary-section {
  margin-top: 20px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.3s ease;
}
.summary-section:hover {
  border-color: rgba(0, 180, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
}

.summary-grid {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.summary-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  position: relative;
  transition: all 0.3s ease;
}
.summary-item:not(:last-child)::after {
  content: "";
  position: absolute;
  right: 0;
  top: 20%;
  height: 60%;
  width: 1px;
  background: rgba(255, 255, 255, 0.06);
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}
.summary-label .label-icon {
  font-size: 14px;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.75);
  font-family: "SF Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

/* 手续费特殊样式 */
.summary-item:not(.total-item):hover .summary-value {
  color: rgba(255, 255, 255, 0.9);
}

.fee-value {
  color: rgba(255, 165, 0, 0.6);
  font-size: 20px;
}

/* 可用余额特殊样式 */
.balance-item {
  flex: none;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 10px 14px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px dashed rgba(0, 180, 255, 0.25);
  border-radius: 8px;
}

.balance-item .summary-label {
  margin-bottom: 0;
}

.balance-value {
  color: #4fc3ff;
  font-size: 20px;
}

/* 总计高亮 */
.total-item {
  background: linear-gradient(
    135deg,
    rgba(255, 215, 0, 0.06),
    rgba(255, 165, 0, 0.03)
  );
  border-radius: 10px;
  padding: 8px 16px;
  border: 1px solid rgba(255, 215, 0, 0.08);
}
.total-item::after {
  display: none !important;
}

.total-value {
  font-size: 26px;
  background: linear-gradient(135deg, #ffd700, #ff8c00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 900;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .summary-section {
    padding: 12px 14px;
  }
  .summary-grid {
    flex-direction: column;
    gap: 6px;
  }
  .summary-item:not(:last-child)::after {
    display: none;
  }
  .summary-item {
    flex-direction: row;
    justify-content: space-between;
    padding: 6px 8px;
  }
  .summary-item .summary-label {
    margin-bottom: 0;
    font-size: 13px;
  }
  .summary-value {
    font-size: 18px;
  }
  .fee-value {
    font-size: 16px;
  }
  .total-value {
    font-size: 20px;
  }
  .total-item {
    padding: 8px 12px;
    margin-top: 4px;
  }
}

@media (max-width: 480px) {
  .summary-section {
    padding: 10px 10px;
  }
  .summary-item {
    padding: 4px 6px;
  }
  .summary-label {
    font-size: 12px;
  }
  .summary-value {
    font-size: 16px;
  }
  .total-value {
    font-size: 18px;
  }
}
.xxb {
  color: #ffc107;
  font-size: 15px;
  cursor: pointer;
}
.xxb:hover {
  color: rgba(255, 255, 255, 0.6);
}

@media (max-width: 1180px) {
  .info-layout {
    grid-template-columns: 208px minmax(0, 1fr);
  }
  .prod-head {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .head-buy {
    grid-column: 2;
    justify-self: end;
    margin-top: 4px;
  }
}

@media (max-width: 980px) {
  .info-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .region-nav {
    position: static;
    padding: 12px;
    min-height: auto;
    height: auto;
  }
  .region-nav-head {
    padding: 2px 6px 10px;
  }
  .region-list {
    flex: none;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;
  }
  .region-list::-webkit-scrollbar {
    display: none;
  }
  .region-item {
    flex: 0 0 auto;
    width: auto;
    grid-template-columns: auto minmax(0, 1fr);
  }
  .rv-price {
    display: none;
  }
  .doc-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 700px) {
  .info-page {
    padding: 24px 12px 56px;
  }
  .info-layout {
    gap: 14px;
  }
  .detail-wrap {
    gap: 14px;
  }
  .prod-head {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
    padding: 20px;
  }
  .head-price {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    flex-wrap: wrap;
  }
  .hp-label {
    text-align: left;
  }
  .head-buy {
    grid-column: 1;
    justify-self: stretch;
  }
  .buy-btn {
    width: 100%;
    min-width: 0;
  }
  .trend-card,
  .doc-card {
    padding: 18px;
    border-radius: 16px;
  }
  .stat-row {
    grid-template-columns: repeat(auto-fit, minmax(104px, 1fr));
  }
}

@media (max-width: 600px) {
  .purchase-dialog >>> .el-dialog__header {
    padding: 16px 18px 12px;
  }
  .purchase-dialog >>> .el-dialog__body {
    padding: 16px 16px 4px;
  }
  .purchase-dialog >>> .el-dialog__footer {
    padding: 12px 16px 18px;
    display: flex;
    gap: 10px;
  }
  .purchase-dialog >>> .dialog-footer .el-button {
    flex: 1;
    padding: 12px 10px;
  }
  .buy-dialog {
    max-height: none;
  }
  .period-grid {
    grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  }
  .form-row {
    grid-template-columns: minmax(0, 1fr);
  }
  .profit-row,
  .pay-row {
    gap: 12px;
  }
}

/* ==========================================================
   主内容 Tab：价格趋势 / 产品文档
   ========================================================== */
.stage-card {
  border-radius: 20px;
  background: linear-gradient(
    175deg,
    rgba(255, 255, 255, 0.05),
    rgba(255, 255, 255, 0.02)
  );
  border: 1px solid var(--line);
  padding: 20px 24px 26px;
  height: 100%;
}
.stage-tabs {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--line);
  margin-bottom: 18px;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.stage-tabs::-webkit-scrollbar {
  display: none;
}
.stage-tab {
  flex: 0 0 auto;
  padding: 9px 22px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--weak);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.22s ease;
  white-space: nowrap;
  line-height: 1.4;
}
.stage-tab:hover {
  color: var(--text);
  background: rgba(255, 255, 255, 0.05);
}
.stage-tab.active {
  background: var(--grad);
  color: #fff;
  box-shadow: 0 8px 22px rgba(37, 99, 235, 0.28);
}
.stage-pane {
  min-width: 0;
}
.stage-pane .doc-grid {
  grid-template-columns: minmax(0, 1fr);
  gap: 18px;
}
.stage-pane .doc-card {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  border-color: rgba(255, 255, 255, 0.06);
}
.stage-pane .chart-stage {
  margin-top: 18px;
}
@media (max-width: 700px) {
  .stage-card {
    padding: 16px 14px 18px;
    border-radius: 16px;
  }
  .stage-tabs {
    width: 100%;
    margin-bottom: 12px;
  }
  .stage-tab {
    flex: 1;
    text-align: center;
  }
}
</style>
